import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ImageWithFallback from '../imageWithFalback';
import { useNavigate } from 'react-router-dom';
import { useFillLastRowWithDuplicates } from '../../hooks/useFillLastRowWithDuplicates';

interface CollectionItem {
    id: string | number;
    name: string;
    description: string;
    image: string;
    serialNumber?: string;
    model?: string;
    size?: string;
    price?: string;
    collectionId?: string;
}

interface CollectionProps {
    items: CollectionItem[];
    title?: string;
    className?: string;
    collectionId?: string;
    clickable?: boolean;
    buildItemLink?: (item: CollectionItem) => string;
    fillIncompleteLastRow?: boolean;
}

function Collection({
    items,
    title,
    className = '',
    collectionId,
    clickable = true,
    buildItemLink,
    fillIncompleteLastRow = false,
}: CollectionProps) {
    const navigate = useNavigate();
    const [emptyBoxesNeeded, setEmptyBoxesNeeded] = useState(0);
    const columnsPerRow = 3;

    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerChildren = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    // Function to determine the scaling ratio based on model and size
    const getWatchScaleRatio = (model?: string, size?: string): number => {
        if (!model || !size) return 0.75; // Default ratio to match original size

        const modelLower = model.toLowerCase();
        const sizeLower = size.toLowerCase();

        // Lucidity
        if (modelLower.includes('lucidity')) {
            if (sizeLower.includes('gents')) return 0.75; // 100% - baseline (adjusted to match original)
            if (sizeLower.includes('ladies')) return 0.6; // 80% of baseline
        }

        // Introsistence
        if (modelLower.includes('introsistence')) {
            if (sizeLower.includes('oversize')) return 0.83; // 111% of baseline
            if (sizeLower.includes('gents') || sizeLower.includes('challenge')) return 0.78; // 104% of baseline
            if (sizeLower.includes('mediums') || sizeLower.includes('medium')) return 0.70; // 93% of baseline
            if (sizeLower.includes('ladies')) return 0.64; // 85% of baseline
        }

        // Determination
        if (modelLower.includes('determination')) {
            if (sizeLower.includes('gents')) return 0.75; // 100% of baseline
            if (sizeLower.includes('mediums') || sizeLower.includes('medium')) return 0.68; // 90% of baseline
            if (sizeLower.includes('ladies')) return 0.6; // 80% of baseline
        }

        // Realization
        if (modelLower.includes('realization')) {
            if (sizeLower.includes('flat') || sizeLower.includes('domes')) return 0.75; // 100% of baseline
        }

        // Connaissance
        if (modelLower.includes('connaissance')) {
            if (sizeLower.includes('gents')) return 0.74; // 99% of baseline
        }

        // Essence d'Esthétique
        if (modelLower.includes('essence')) {
            if (sizeLower.includes('ladies')) return 0.6; // 80% of baseline
        }

        return 0.75; // Default ratio to match original size
    };

    const {
        containerRef,
        items: displayItems,
        duplicatesApplied,
    } = useFillLastRowWithDuplicates(
        items,
        {
            enable: fillIncompleteLastRow,
            minItems: 11,
            itemSelector: '.collection-item-card',
            offsetPercent: 0.2,
            randomizeSelection: fillIncompleteLastRow,
        }
    );

    const shouldHidePlaceholders = fillIncompleteLastRow && duplicatesApplied;

    useEffect(() => {
        if (shouldHidePlaceholders) {
            setEmptyBoxesNeeded(0);
            return;
        }
        const totalItems = displayItems.length;
        const itemsInLastRow = totalItems % columnsPerRow;
        setEmptyBoxesNeeded(itemsInLastRow === 0 ? 0 : columnsPerRow - itemsInLastRow);
    }, [displayItems, columnsPerRow, shouldHidePlaceholders]);

    const handleItemClick = (item: CollectionItem) => {
        if (!clickable) {
            return;
        }

        if (buildItemLink) {
            navigate(buildItemLink(item));
            return;
        }

        if (collectionId) {
            const id = item.id.toString().toUpperCase();
            navigate(`/collections/${collectionId}/${id}`);
        }
    };

    return (
        <section className={`w-full ${className}`}>
            {title && (
                <motion.h2
                    className="text-2xl md:text-3xl font-light mb-12 text-center"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                >
                    {title}
                </motion.h2>
            )}
            <motion.div
                variants={staggerChildren}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                key={displayItems.length}
                className="border-t border-l border-[#747473FF]"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" ref={containerRef}>
                    {displayItems.map((item, index) => {
                        const scaleRatio = getWatchScaleRatio(item.model, item.size);
                        const scalePercentage = Math.round(scaleRatio * 100);
                        
                        return (
                            <motion.div
                                key={`${item.id}-${index}`}
                                onClick={() => handleItemClick(item)}
                                className={`${clickable ? 'cursor-pointer' : 'cursor-default'} collection-item-card group p-8 md:p-12 
                                    border-r ${index === displayItems.length - 1 ? 'md:border-b' : 'border-b'} border-[#747473FF] transition-colors duration-300`}
                                variants={fadeIn}
                            >
                                <div className="relative aspect-square mb-8 overflow-hidden flex items-center justify-center">
                                    <div 
                                        className="relative"
                                        style={{ 
                                            width: `${scalePercentage}%`,
                                            maxWidth: '100%'
                                        }}
                                    >
                                        <ImageWithFallback
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-auto object-contain transition-transform duration-300 group-hover:scale-105"
                                        />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-sm uppercase tracking-wider mb-2">{item.name}</h3>
                                    <p className="text-xs text-gray-800 font-light">{item.description}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                    {/* Add empty boxes to complete the last row - only visible on md and lg screens */}
                    {!shouldHidePlaceholders && Array.from({ length: emptyBoxesNeeded }).map((_, index) => (
                        <motion.div
                            key={`empty-${index}`}
                            className="hidden md:block p-8 md:p-12 border-r border-b border-[#747473FF]"
                            variants={fadeIn}
                        >
                            <div className="relative aspect-square mb-8 overflow-hidden" />
                            <div className="text-center">
                                <div className="h-6 mb-2" />
                                <div className="h-4" />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}

export default Collection; 
